import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WineCard from "@/components/WineCard";
import WineDetailDialog from "@/components/WineDetailDialog";
import { TYPE_LABEL } from "@/components/AcornPlaceholder";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Info, Map, GlassWater } from "lucide-react";
import italyMapImg from "@/assets/brand/italy-map.webp";

const TYPES = ["rosso", "bianco", "rosato", "bollicine", "dolce"];

export default function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [openWine, setOpenWine] = useState(null);
    const [spiritsCount, setSpiritsCount] = useState(0);

    const isAnon = !user;
    const isUnverified = user && !user.email_verified;
    const canSeePrices = true;  // Fase 6.8: listino pubblico sempre visibile.
    const isTradeVipUnverified = isUnverified && (user?.role === "trade" || user?.role === "vip");

    useEffect(() => {
        api.get("/wines/featured")
            .then(({ data }) => setFeatured(data.items || []))
            .finally(() => setLoading(false));
        api.get("/wines/categorie")
            .then(({ data }) => setSpiritsCount(data.non_vino || 0))
            .catch(() => {});
    }, []);

    const goToCatalog = (extraParams = {}) => {
        const p = new URLSearchParams();
        if (query.trim()) p.set("q", query.trim());
        Object.entries(extraParams).forEach(([k, v]) => v && p.set(k, v));
        const qs = p.toString();
        navigate(qs ? `/carta?${qs}` : "/carta");
    };

    const submitSearch = (e) => {
        e.preventDefault();
        goToCatalog();
    };

    // Fase 6.8: banner "Accedi per prezzi" rimosso — listino pubblico sempre visibile.
    let notice = null;

    return (
        <main>
            {/* Fase 7.1: hero rimosso. Il cliente ha chiesto di togliere
                "La Selezione del Sommelier". Le card in evidenza restano
                come primo contenuto della home, in griglia compatta. */}

            {/* Featured — titolo sobrio + griglia iper-compatta (Fase 7.1.1) */}
            <section className="co-container pt-8 sm:pt-12 pb-10" data-testid="home-featured-section">
                {featured.length > 0 && !loading && (
                    <div className="mb-4 sm:mb-5">
                        <span className="co-caption block">Vini consigliati</span>
                    </div>
                )}
                {loading ? (
                    <div className="text-center text-[hsl(var(--muted-foreground))] py-12">
                        Caricamento…
                    </div>
                ) : featured.length === 0 ? (
                    <div className="text-center text-[hsl(var(--muted-foreground))] py-12">
                        Nessuna selezione al momento. Torna presto.
                    </div>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                        {featured.map((w) => (
                            <WineCard
                                key={w.id}
                                wine={w}
                                onOpen={() => setOpenWine(w.id)}
                                size="compact"
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Fase 6.8: banner rimosso — listino pubblico sempre visibile */}

            {/* Search & explore */}
            <section className="co-container py-14 border-t border-[hsl(var(--border))]" data-testid="home-explore-section">
                <div className="max-w-3xl mx-auto text-center">
                    <span className="co-caption block mb-3">Esplora la Carta</span>
                    <h2 className="text-3xl sm:text-4xl mb-8">
                        Trova <em className="italic text-[hsl(var(--primary))]">il tuo</em> vino
                    </h2>

                    <form onSubmit={submitSearch} className="mb-8" data-testid="home-search-form">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                            <Input
                                data-testid="home-search-input"
                                placeholder="Cerca per nome, produttore, regione…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-12 pr-4 h-14 text-base bg-white"
                            />
                        </div>
                    </form>

                    <div className="flex items-center justify-center gap-2 flex-wrap mb-6" data-testid="home-type-pills">
                        {TYPES.map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => goToCatalog({ tipo: t })}
                                data-testid={`home-pill-${t}`}
                                className="px-5 py-2 border border-[hsl(var(--border))] rounded-full text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors bg-white"
                            >
                                {TYPE_LABEL[t]}
                            </button>
                        ))}
                    </div>

                    {/* Fase 7.6: rimosso link "Distillati & Liquori · N etichette"
                        (l'header ha già la voce "Distillati" e il footer/menu
                        offrono alternative — evitiamo la ridondanza). */}

                    {/* Fase 7.5: CTA "Tutta la Carta" prominente — bottone
                        pieno bordeaux con testo crema, generoso ma coerente
                        col design. Non più un link secondario. */}
                    <button
                        onClick={() => navigate("/carta")}
                        data-testid="home-full-catalog-btn"
                        className="inline-flex items-center gap-3 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-8 sm:px-10 py-4 sm:py-5 rounded-sm text-base sm:text-lg tracking-wide shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all"
                    >
                        Tutta la Carta <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
            </section>

            {/* Fase 7.5: Territory map tile con illustrazione cartografica
                Italia vintage (crema/bordeaux, servita locale WebP 68KB).
                Fade orizzontale verso destra per far respirare il testo. */}
            <section className="co-container pb-16" data-testid="home-map-tile-section">
                <button
                    onClick={() => navigate("/mappa")}
                    className="w-full group relative overflow-hidden rounded-sm border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] transition-all text-left flex flex-col md:flex-row items-stretch shadow-sm hover:shadow-md bg-[#F5EFE7]"
                    data-testid="home-map-tile"
                >
                    <div className="relative w-full md:w-1/2 h-44 sm:h-56 md:h-72 shrink-0 overflow-hidden">
                        <img
                            src={italyMapImg}
                            alt="Illustrazione dell'Italia in stile cartografico"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500"
                        />
                        {/* Fade verso il testo: destra su desktop, in basso su mobile */}
                        <div
                            className="absolute inset-0 pointer-events-none hidden md:block"
                            style={{
                                background:
                                    "linear-gradient(to right, transparent 55%, #F5EFE7 100%)",
                            }}
                        />
                        <div
                            className="absolute inset-0 pointer-events-none md:hidden"
                            style={{
                                background:
                                    "linear-gradient(to bottom, transparent 60%, #F5EFE7 100%)",
                            }}
                        />
                    </div>
                    <div className="flex-1 p-6 sm:p-8 md:p-10 flex flex-col justify-center">
                        <span className="co-caption block mb-2 text-[hsl(var(--primary))]">Esplora la carta</span>
                        <h3 className="text-3xl sm:text-4xl md:text-5xl leading-tight mb-3 text-[hsl(var(--foreground))]">
                            La mappa della carta
                        </h3>
                        <p className="text-sm sm:text-base text-[hsl(var(--muted-foreground))] max-w-lg mb-5">
                            Parti dai territori: regioni italiane, Champagne, Bourgogne, Bordeaux, Piemonte, Toscana. Scopri i produttori zona per zona.
                        </p>
                        <span className="self-start inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white rounded-full shadow-md px-5 py-2.5 text-sm tracking-wide group-hover:shadow-lg group-hover:gap-3 transition-all">
                            Apri la mappa <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                    </div>
                </button>
            </section>

            <WineDetailDialog
                wineId={openWine}
                open={Boolean(openWine)}
                onOpenChange={(v) => !v && setOpenWine(null)}
                canSeePrices={canSeePrices}
                isTradeVipUnverified={isTradeVipUnverified}
            />
        </main>
    );
}
